import { toast as sonnerToast } from 'sonner';
import i18n from '../i18n/i18n';

const DEFAULT_DURATION = 4000;
const ERROR_DURATION = 6000;
const SUCCESS_DURATION = 3000;

export interface ToastOptions {
  duration?: number;
  description?: string;
  action?: { label: string; onClick: () => void };
  closeButton?: boolean;
  id?: string | number;
  [key: string]: unknown;
}

interface ToastEntry {
  id: number;
  type: string;
  message: string;
  options: ToastOptions;
  timestamp: string;
}

type ToastListener = (entry: ToastEntry) => void;

interface PromiseConfig<T = unknown> {
  loading?: string;
  success?: string | ((data: T) => string);
  error?: string | ((err: unknown) => string);
  [key: string]: unknown;
}

class ToastService {
  private _listeners: ToastListener[] = [];
  private _history: ToastEntry[] = [];
  private _maxHistory = 50;

  private _logToast(type: string, message: string, options: ToastOptions = {}) {
    const entry: ToastEntry = {
      id: Date.now(),
      type,
      message,
      options,
      timestamp: new Date().toISOString(),
    };

    this._history.unshift(entry);
    if (this._history.length > this._maxHistory) {
      this._history.pop();
    }

    this._listeners.forEach((listener) => listener(entry));

    if (process.env.NODE_ENV === 'development') {
      console.debug(`[Toast:${type}]`, message, options);
    }
  }

  private _createOptions(type: 'success' | 'error' | 'info' | 'warning', options: ToastOptions = {}): ToastOptions {
    const defaultDurations: Record<string, number> = {
      success: SUCCESS_DURATION,
      error: ERROR_DURATION,
      info: DEFAULT_DURATION,
      warning: DEFAULT_DURATION,
    };

    return {
      duration: options.duration ?? defaultDurations[type],
      ...options,
    };
  }

  success(message: string, options: ToastOptions = {}) {
    this._logToast('success', message, options);
    return sonnerToast.success(message, this._createOptions('success', options));
  }

  error(message: string, options: ToastOptions = {}) {
    this._logToast('error', message, options);
    return sonnerToast.error(message, this._createOptions('error', options));
  }

  info(message: string, options: ToastOptions = {}) {
    this._logToast('info', message, options);
    return sonnerToast.info(message, this._createOptions('info', options));
  }

  warning(message: string, options: ToastOptions = {}) {
    this._logToast('warning', message, options);
    return sonnerToast.warning(message, this._createOptions('warning', options));
  }

  loading(message: string, options: ToastOptions = {}) {
    this._logToast('loading', message, options);
    return sonnerToast.loading(message, {
      duration: Infinity,
      ...options,
    });
  }

  promise<T>(promise: Promise<T>, config: PromiseConfig<T> = {}) {
    const {
      loading = i18n.t('common:loading'),
      success = i18n.t('common:success.saved'),
      error = i18n.t('common:error.generic'),
      ...options
    } = config;

    this._logToast('promise', `${loading} → ${success} | ${error}`, options as ToastOptions);

    return sonnerToast.promise(promise, {
      loading,
      success: (data: T) => {
        if (typeof success === 'function') {
          return success(data);
        }
        return success;
      },
      error: (err: any) => {
        if (typeof error === 'function') {
          return error(err);
        }
        const errorMessage = err?.response?.data?.detail || err?.response?.data?.message || err?.message || error;
        return errorMessage;
      },
      ...options,
    });
  }

  custom(content: string | React.ReactNode, options: ToastOptions = {}) {
    this._logToast('custom', typeof content === 'string' ? content : '[Custom Component]', options);
    return sonnerToast(content as string, options);
  }

  action(message: string, actionConfig: { label: string; onClick: () => void }, options: ToastOptions = {}) {
    const { label, onClick } = actionConfig;
    this._logToast('action', message, { ...options });

    return sonnerToast(message, {
      ...this._createOptions('info', options),
      action: {
        label,
        onClick,
      },
    });
  }

  dismiss(toastId?: string | number) {
    if (toastId) {
      sonnerToast.dismiss(toastId);
    } else {
      sonnerToast.dismiss();
    }
  }

  subscribe(listener: ToastListener): () => void {
    this._listeners.push(listener);
    return () => {
      this._listeners = this._listeners.filter((l) => l !== listener);
    };
  }

  getHistory(): ToastEntry[] {
    return [...this._history];
  }

  clearHistory() {
    this._history = [];
  }

  saved(message: string = i18n.t('common:success.saved')) {
    return this.success(message);
  }

  deleted(message: string = i18n.t('common:success.deleted')) {
    return this.success(message);
  }

  copied(message: string = i18n.t('common:success.copied')) {
    return this.success(message, { duration: 2000 });
  }

  networkError(message: string = i18n.t('common:error.network')) {
    return this.error(message);
  }

  validationError(message: string = i18n.t('common:error.validation')) {
    return this.warning(message);
  }

  unauthorized(message: string = i18n.t('common:error.unauthorized')) {
    return this.error(message);
  }

  notFound(message: string = i18n.t('common:error.not_found')) {
    return this.error(message);
  }
}

const toastService = new ToastService();

export const toast = toastService;

export default toastService;
